import Oidc from 'oidc-client';
import URL from 'url-parse';
import Clients from './clients';
import AppSettings from './app.settings';

class Auth {
  constructor() {
    this.url = new URL(window.location.href, true);

    // check localstorage for a hacky token for UR
    this.accessToken = localStorage.getItem('one-time-access-token');

    if (this.accessToken !== null) {
      localStorage.removeItem('one-time-access-token');
    }

    this.baseOptions = {
      authority: process.env.AUTH_SERVER_URL,
      client_id: null,
      redirect_uri: null,
      post_logout_redirect_uri: null,
      silent_redirect_uri: `${this.url.protocol}//${this.url.host}/home/silentrefresh/`,
      automaticSilentRenew: true,
      loadUserInfo: false,
      response_type: 'id_token token',
      scope: 'openid profile email core tenant_metadata',
    };

    this.options = {};
  }

  getUrl() {
    return this.url;
  }

  hasAccessToken() {
    return this.accessToken !== null;
  }

  checkForcedLogout() {
    if (this.url.query.hasOwnProperty('forcelogout') && this.url.query.forcelogout === 'true') {
      this.manager.removeUser();
      this.manager.signinRedirect(this.baseOptions);
      return true;
    }

    if (this.url.pathname.includes('logoff') || this.url.pathname.includes('logout')) {
      this.signUserOut();
      this.manager.signinRedirect(this.baseOptions);
      return true;
    }

    return false;
  }

  async init() {
    return new Promise((resolve, reject) => {
      // If we already have an access token from the URL, use that instead
      if (this.hasAccessToken()) {
        localStorage.setItem('portal-bypass', true);
        reject();
        return;
      }

      /**
       * At some point we're going to have to account for other
       * subdomains and change the clientid based on them
       */
      if (this.url.query.hasOwnProperty('otp')) {
        this.baseOptions.acr_values = `otp:${this.url.query.otp}`;
        this.baseOptions.prompt = 'login';
      }

      this.manager = this.initializeManager();

      if (this.url.query.hasOwnProperty('otp')) {
        this.manager.removeUser();
        this.manager.signinRedirect(this.baseOptions);
        return;
      }

      if (this.checkForcedLogout()) {
        return;
      }

      this.manager.events.addSilentRenewError(() => {
        this.startAuthentication();
      });

      this.manager.events.addUserLoaded((user) => {
        this.user = user;
      });

      if (process.env.NODE_ENV !== AppSettings.environments.production) {
        Oidc.Log.logger = console;
      }

      resolve();
    });
  }

  initializeManager() {
    const client = this.detectClient();

    this.options = {
      ...this.baseOptions,
      ...client,
    };

    return new Oidc.UserManager(this.options);
  }

  detectClient() {
    return Clients.load(this.url.host);
  }

  switchAccount(accountId) {
    this.options.acr_values = `tenant:${accountId}`;
    this.options.prompt = 'login';

    this.manager.removeUser();
    this.manager.signinRedirect(this.options);
  }

  async userIsLoggedIn() {
    try {
      await this.manager.signinRedirectCallback();
    } catch (error) {
      // noop
    }

    this.user = await this.manager.getUser();
    return new Promise((resolve) => {
      if (this.user != null && !this.user.expired) {
        return resolve(this.user);
      }

      return resolve(null);
    });
  }

  signUserOut() {
    this.manager.signoutRedirect();
  }

  getClaims() {
    if (!this.user) {
      return false;
    }

    return this.user.profile;
  }

  setUser(user) {
    this.user = user;
  }

  getBrandClaim() {
    return this.user.profile && this.user.profile['tenant:brand']
      ? this.user.profile['tenant:brand']
      : null;
  }

  getAuthorizationHeader() {
    if (this.hasAccessToken()) {
      return `Bearer ${this.accessToken}`;
    }

    return `${this.user.token_type} ${this.user.access_token}`;
  }

  startAuthentication() {
    // https://stackoverflow.com/questions/36953762/identity-server-3-mvc-client-state-parameter
    localStorage.setItem('sign-in-redirect-uri', window.location.href);
    return this.manager.signinRedirect();
  }

  getExpiry() {
    const token = this.accessToken || this.user.access_token;
    const { exp } = JSON.parse(Buffer.from(token.split('.')[1], 'base64').toString());
    return exp;
  }
}

export default new Auth();
